/*
 * Copyright 2026 Sony Corporation
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import type { DeviceOp } from '@ips-device-verse/twin-service/schema/device-ops';
import type { RealtimeEvent } from '@ips-device-verse/twin-service/schema/realtime';

import { createAgentClient, type AgentClient } from '../services/agent-client';
import type { DeviceDigest } from '../device-ops/digest';
import type { RealtimeClient } from '../services/realtime-client';

export interface UseDeviceAgentOptions {
  /** The world's socket, which the agent answers on rather than opening its own. */
  socket: Pick<RealtimeClient, 'send'>;
  subscribe: (listener: (event: RealtimeEvent) => void) => () => void;
}

export interface UseDeviceAgentResult {
  requestOps: AgentClient<DeviceOp, DeviceDigest>['requestOps'];
  newThread: () => void;
  /** The turn being written, so the panel can show it arriving rather than appearing. */
  streaming: string | undefined;
}

/**
 * The Device page's end of the agent (devplan §6).
 *
 * The same client the Scene page uses, over the socket this page already has
 * open. What differs is the vocabulary, and that is decided by the socket the
 * turn arrives on — a world's offers device verbs and nothing else.
 */
export const useDeviceAgent = ({ socket, subscribe }: UseDeviceAgentOptions): UseDeviceAgentResult => {
  const [streaming, setStreaming] = useState<string>();
  const clientRef = useRef<AgentClient<DeviceOp, DeviceDigest>>(null);

  useEffect(() => {
    clientRef.current = createAgentClient<DeviceOp, DeviceDigest>({
      socket,
      subscribe,
      onDelta: setStreaming,
    });
  }, [socket, subscribe]);

  return useMemo(
    () => ({
      requestOps: async (prompt, digest) => {
        setStreaming('');
        try {
          if (!clientRef.current) {
            throw new Error('The assistant is not connected yet.');
          }
          return await clientRef.current.requestOps(prompt, digest);
        } finally {
          setStreaming(undefined);
        }
      },
      newThread: () => clientRef.current?.newThread(),
      streaming,
    }),
    [streaming]
  );
};
